import { defineComponent } from "vue";
import { createRouter, createWebHistory } from "vue-router";
import LangContext from "./LangContext.vue";
import HomePage from "../views/HomePage.vue";
import AboutUs from "../views/AboutUs.vue";
import ProductListing from "../views/ProductListing.vue";
import NotFound from "../views/NotFound.vue";
import type {
  RouteRecordRaw,
  RouteLocationNormalized,
  NavigationGuardNext,
} from "vue-router";
import { I18n } from "vue-i18n";
import i18n, { setLocale, getLanguages } from "../i18n/index";
import { LANG } from "vacv-common";
import { getEnvVar } from "@/utils";

const WEBSITE_CONTEXT = getEnvVar("VITE_WEBSITE_CONTEXT");

const pagePaths = {
  en: {
    productListing: "/en" + WEBSITE_CONTEXT + "/promotions/product-listing",
    allDeals: "/en" + WEBSITE_CONTEXT + "/all-deals/:promotionPath",
    allExtraPerks:
      "/en" + WEBSITE_CONTEXT + "/plan-your-trip/hotel-partners/:promotionPath",
    allVacationTypes:
      "/en" + WEBSITE_CONTEXT + "/plan-your-trip/collections/:promotionPath",
    allInterests:
      "/en" +
      WEBSITE_CONTEXT +
      "/plan-your-trip/travel-inspiration/:promotionPath",
  },
  fr: {
    productListing: "/fr" + WEBSITE_CONTEXT + "/promotions/produits-liste",
    allDeals: "/fr" + WEBSITE_CONTEXT + "/toutes-les-offres/:promotionPath",
    allExtraPerks:
      "/fr" +
      WEBSITE_CONTEXT +
      "/planifier-votre-voyage/partenaires-hoteliers/:promotionPath",
    allVacationTypes:
      "/fr" +
      WEBSITE_CONTEXT +
      "/planifier-votre-voyage/collections/:promotionPath",
    allInterests:
      "/fr" +
      WEBSITE_CONTEXT +
      "/planifier-votre-voyage/inspiration-voyage/:promotionPath",
  },
};

interface RoutePaths {
  path: string;
  name: string;
  component: ReturnType<typeof defineComponent>;
  alias: string | string[];
  meta?: {
    pushEventTitle?: string;
    referrer: string;
  };
}

export const childrenPaths: Array<RoutePaths> = [
  {
    path: "/:lang/promotions",
    name: "Home",
    component: HomePage,
    alias: "/:lang/promotions",
  },
  {
    path: "/:lang/promotions/about",
    name: "About",
    component: AboutUs,
    alias: "/:lang/promotions/about",
  },
  {
    path: "/:lang" + pagePaths.en.productListing.substring(3),
    name: "ProductListing",
    component: ProductListing,
    alias: "/:lang" + pagePaths.fr.productListing.substring(3),
    meta: { pushEventTitle: "Deals Page", referrer: "" },
  },
  {
    path: "/:lang" + pagePaths.en.allDeals.substring(3),
    name: "AllDeals",
    component: ProductListing,
    alias: "/:lang" + pagePaths.fr.allDeals.substring(3),
    meta: { pushEventTitle: "Deals Page", referrer: "" },
  },
  {
    path: "/:lang" + pagePaths.en.allExtraPerks.substring(3),
    name: "AllExtraPerks",
    component: ProductListing,
    alias: "/:lang" + pagePaths.fr.allExtraPerks.substring(3),
    meta: { pushEventTitle: "Deals Page", referrer: "" },
  },
  {
    path: "/:lang" + pagePaths.en.allVacationTypes.substring(3),
    name: "AllVacationTypes",
    component: ProductListing,
    alias: "/:lang" + pagePaths.fr.allVacationTypes.substring(3),
    meta: { pushEventTitle: "Deals Page", referrer: "" },
  },
  {
    path: "/:lang" + pagePaths.en.allInterests.substring(3),
    name: "AllInterests",
    component: ProductListing,
    alias: "/:lang" + pagePaths.fr.allInterests.substring(3),
    meta: { pushEventTitle: "Deals Page", referrer: "" },
  },
];

export const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    redirect: "/en/promotions",
    name: "Root",
  },
  {
    path: "/:lang",
    component: LangContext,
    beforeEnter(
      to: RouteLocationNormalized,
      _from: RouteLocationNormalized,
      next: NavigationGuardNext
    ) {
      const language = to.path.split("/")[1] as LANG;
      const supportedLanguagesString = getEnvVar("VITE_I18N_LANGUAGES");
      if (getLanguages(supportedLanguagesString).indexOf(language) > -1) {
        setLocale(i18n as I18n, language, supportedLanguagesString);
        return next();
      }
      return next({ path: "Root" });
    },
    children: childrenPaths,
  },
  { path: "/:pathMatch(.*)*", name: "NotFound", component: NotFound },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: routes,
});

router.afterEach((_, from: { fullPath: string }) => doAfterEach(from.fullPath));

const doAfterEach = (fullPath: string) => {
  if (fullPath.length && fullPath !== "/") {
    router.currentRoute.value.meta.referrer = window.location.origin + fullPath;
  } else if (document.referrer) {
    router.currentRoute.value.meta.referrer = document.referrer;
  }
};

export default router;
